/**
 * shell/topbar.tsx — admin-shell topbar.
 * Surface title + active-queue picker + "Preview public" launcher + data-source
 * pill. Identity/sign-out live at the FOOT of the sidebar, not here. The
 * launcher is the sole build-time affordance (opens the public Guest / Now
 * Serving surfaces in a new tab so they render standalone, exactly as a guest
 * phone / door TV would, without disturbing this admin window).
 */

import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';
import { useSurfaceRouter } from '../navigation/surfaceRouter';
import { getSurface, previewablePublicSurfaces } from '../queue/surfaces';
import { useDataSource } from '../queue/dataSource';
import { useActiveQueue } from '../queue/activeQueue';
import { Mono } from '../queue/components/atoms';

/**
 * Open a public surface (Guest / Now Serving) for preview — as a REAL public
 * URL for the active queue. A public surface has no login, so it resolves its
 * queue purely from the URL: `?surface=<key>&org=<orgId>&q=<slug>` (see
 * queue/guestSession readLink). We build a CLEAN url (origin only) with just
 * those params — copying the current admin URL would drag in admin-only params
 * and, crucially, would omit org+slug so the public page loads queue-less/empty.
 *
 * On web we open it in a NEW TAB so the admin window keeps its state; on native
 * (no tabs) we navigate the current router and rootNavigator renders standalone.
 */
function openPublicPreview(
  key: string,
  orgId: string,
  slug: string,
  setSurface: (s: any) => void,
) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const url = new URL(window.location.origin);
    url.searchParams.set('surface', key);
    url.searchParams.set('org', orgId);
    url.searchParams.set('q', slug);
    window.open(url.toString(), '_blank', 'noopener');
    return;
  }
  setSurface(key);
}

export default function Topbar({ showMenu = false, onMenu }: { showMenu?: boolean; onMenu?: () => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { entitlements, org } = useSession();
  const { surface, setSurface } = useSurfaceRouter();
  const { mode } = useDataSource();
  const { activeQueue } = useActiveQueue();
  const [pOpen, setPOpen] = useState(false);
  const { width } = useWindowDimensions();
  const narrow = width < 560; // condense the right-side controls on small screens

  const title = getSurface(surface).label;

  // Public surfaces an admin can preview (entitlement-gated). Rendered as a
  // build-time launcher, never as sidebar destinations.
  const publicSurfaces = previewablePublicSurfaces(entitlements).map(getSurface);
  // A public preview only works with an org + the active queue's slug (the
  // public page resolves its queue from the URL). Without both, the launched
  // page would be empty — so we require them and disable the launcher otherwise.
  const previewOrg = org?.id ?? null;
  const previewSlug = activeQueue?.slug ?? null;
  const canPreviewPublic = mode === 'live' && !!previewOrg && !!previewSlug;

  return (
    <View style={{
      height: 56, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20,
      backgroundColor: colors.card, borderBottomWidth: 1, borderBottomColor: colors.line,
      // The topbar's dropdowns (queue picker, DEV preview, user menu) are
      // position:absolute and must paint ABOVE the surface content below. RN-Web
      // gives every View z-index:0/position:relative, so the later-in-DOM surface
      // would otherwise occlude them. Lift the whole topbar into its own higher
      // stacking context. (Web only — native handles overlay order differently.)
      ...(Platform.OS === 'web' ? { position: 'relative', zIndex: 20 } as any : {}),
    }}>
      {/* Hamburger — opens the sidebar drawer on narrow screens. */}
      {showMenu && (
        <TouchableOpacity
          onPress={onMenu}
          accessibilityLabel="Open menu"
          style={{ width: 36, height: 36, marginRight: 8, marginLeft: -6, borderRadius: 9, alignItems: 'center', justifyContent: 'center' }}
        >
          <View style={{ width: 18, height: 2, backgroundColor: colors.ink, borderRadius: 2 }} />
          <View style={{ width: 18, height: 2, backgroundColor: colors.ink, borderRadius: 2, marginTop: 4 }} />
          <View style={{ width: 18, height: 2, backgroundColor: colors.ink, borderRadius: 2, marginTop: 4 }} />
        </TouchableOpacity>
      )}
      <Text style={{ fontSize: 15, fontWeight: '700', color: colors.ink, flexShrink: 1 }} numberOfLines={1}>{title}</Text>

      <View style={{ marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: narrow ? 8 : 12, flexShrink: 0, paddingLeft: 8 }}>
        {/* Active-queue picker removed from the topbar per product decision — the
            live surfaces read the auto-selected queue from ActiveQueueProvider
            (persisted per-org, first-queue fallback), and queue creation lives in
            the Config empty state, so no affordance is lost here. */}

        {/* Preview-public launcher — build-time only. Opens the standalone
            Guest / Now Serving surfaces (new tab on web) so an admin can verify
            them without them ever becoming sidebar destinations. */}
        {publicSurfaces.length > 0 && (
          <View style={{ position: 'relative' }}>
            <TouchableOpacity
              onPress={() => setPOpen(o => !o)}
              accessibilityLabel="Preview public surfaces (Guest, Now Serving)"
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 6, height: 32,
                paddingHorizontal: narrow ? 9 : 12,
                borderRadius: 9, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.paper,
              }}
            >
              {/* functional glyph only — monochrome, no emoji */}
              <Text style={{ fontSize: 12, color: colors.ink3 }}>◱</Text>
              {!narrow && <Mono style={{ fontSize: 9, letterSpacing: 1, color: colors.ink4 }}>PREVIEW</Mono>}
              <Text style={{ fontSize: 10, color: colors.ink4 }}>▾</Text>
            </TouchableOpacity>

            {pOpen && (
              <View style={{
                position: 'absolute', top: 38, right: 0, width: 232, backgroundColor: colors.card,
                borderWidth: 1, borderColor: colors.line, borderRadius: 12, padding: 8,
                ...(Platform.OS === 'web' ? { boxShadow: '0 12px 30px -10px rgba(0,0,0,.25)', zIndex: 50 } as any : {}),
              }}>
                <Text style={menuLabel(colors)}>Preview public surface</Text>
                {publicSurfaces.map(s => (
                  <TouchableOpacity
                    key={s.key}
                    disabled={!canPreviewPublic}
                    onPress={() => {
                      if (!canPreviewPublic || !previewOrg || !previewSlug) return;
                      openPublicPreview(s.key, previewOrg, previewSlug, setSurface);
                      setPOpen(false);
                    }}
                    style={{ paddingHorizontal: 10, paddingVertical: 8, borderRadius: 7, opacity: canPreviewPublic ? 1 : 0.45 }}
                  >
                    <Text style={{ fontSize: 13, color: colors.ink, fontWeight: '500' }}>{s.label}</Text>
                    <Mono style={{ fontSize: 9, letterSpacing: 0.5, color: colors.ink4, marginTop: 2 }}>
                      {canPreviewPublic
                        ? `${s.kicker} · ${activeQueue?.name ?? ''}${Platform.OS === 'web' ? ' ↗' : ''}`
                        : `${s.kicker} · select a live queue first`}
                    </Mono>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Data-source / API reachability pill removed from the topbar per
            product decision — it was operator-facing dev chrome, not customer UI. */}
      </View>
    </View>
  );
}

function menuLabel(colors: any) {
  return { fontSize: 10, fontWeight: '600' as const, textTransform: 'uppercase' as const, letterSpacing: 0.6, color: colors.ink4, paddingHorizontal: 10, paddingVertical: 6 };
}
